import { getRecentLogsOptions } from "@/actions/workout/handler";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { calculateDelta, calculateE1RM } from "@/utils/workout-stats";
import { useQuery } from "@tanstack/react-query";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import {
  Calendar,
  History,
  Loader2,
  PlayCircle,
  TrendingUp,
  Zap,
} from "lucide-react";
import * as React from "react";
import { HistoryChart } from "./HistoryChart";

interface WorkoutHistoryDrawerProps {
  exerciseId: string;
  exerciseName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WorkoutHistoryDrawer({
  exerciseId,
  exerciseName,
  open,
  onOpenChange,
}: WorkoutHistoryDrawerProps) {
  const [activeVideo, setActiveVideo] = React.useState<{
    url: string;
    setNumber: number;
  } | null>(null);
  const { data, isLoading } = useQuery({
    ...getRecentLogsOptions({ exerciseId, limit: 30 }), // Increase limit for chart
    enabled: open && !!exerciseId,
  });

  const logs = data?.body?.logs ?? [];

  // Define types for sessions
  type LogType = (typeof logs)[0];
  type SessionType = {
    date: Date | string;
    dayId: string;
    weekNumber: number | null;
    dayNumber: number | null;
    dayName: string | null;
    sets: LogType[];
    bestE1RM: number;
  };

  // Group logs by date+dayId and calculate sessions
  const sessions = logs.reduce<Record<string, SessionType>>((acc, log) => {
    const dateKey = `${format(new Date(log.loggedAt), "yyyy-MM-dd")}_${log.dayId ?? ""}`;
    if (!acc[dateKey]) {
      acc[dateKey] = {
        date: log.loggedAt,
        dayId: log.dayId ?? "",
        weekNumber: log.weekNumber,
        dayNumber: log.dayNumber,
        dayName: log.dayName,
        sets: [],
        bestE1RM: 0,
      };
    }
    acc[dateKey].sets.push(log);

    // Track best e1RM for this session
    if (log.completedWeight && log.completedReps) {
      const e1rm = calculateE1RM(log.completedWeight, log.completedReps);
      if (e1rm > acc[dateKey].bestE1RM) {
        acc[dateKey].bestE1RM = e1rm;
      }
    }

    return acc;
  }, {});

  // Sort sessions by date descending, then dayId to maintain order within same day
  const sessionList = Object.values(sessions).sort((a, b) => {
    const dateCompare = new Date(b.date).getTime() - new Date(a.date).getTime();
    if (dateCompare !== 0) return dateCompare;
    return b.dayId.localeCompare(a.dayId);
  });

  // Data for the chart
  const chartData = sessionList
    .filter((s) => s.bestE1RM > 0)
    .map((s) => ({
      date: s.date,
      e1RM: s.bestE1RM,
      weekNumber: s.weekNumber,
      dayNumber: s.dayNumber,
      dayName: s.dayName,
    }));

  // Calculate PR and Delta
  const bestEver =
    chartData.length > 0 ? Math.max(...chartData.map((d) => d.e1RM)) : 0;

  const lastSession = chartData.length > 0 ? chartData[0] : null;
  const prevSession = chartData.length > 1 ? chartData[1] : null;

  const progress =
    lastSession && prevSession
      ? calculateDelta(lastSession.e1RM, prevSession.e1RM)
      : null;

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <div className="mx-auto flex h-full w-full max-w-lg flex-col overflow-hidden">
          <DrawerHeader className="border-b pb-4">
            <div className="flex items-center gap-2">
              <History className="h-5 w-5 text-primary" />
              <DrawerTitle>{exerciseName}</DrawerTitle>
            </div>
            <DrawerDescription>
              Geçmiş performans analizi ve gelişim grafiği
            </DrawerDescription>
          </DrawerHeader>

          {/* Performance Summary Badges */}
          {!isLoading && sessionList.length > 0 && (
            <div className="grid grid-cols-2 gap-3 bg-muted/30 p-4">
              <div className="rounded-xl border bg-card p-3 shadow-sm">
                <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                  En İyi e1RM
                </div>
                <div className="flex items-baseline gap-1">
                  <span className="font-bold text-primary text-xl">
                    {bestEver}
                  </span>
                  <span className="text-[10px] text-muted-foreground">kg</span>
                </div>
              </div>
              <div className="rounded-xl border bg-card p-3 shadow-sm">
                <div className="mb-1 text-[10px] text-muted-foreground uppercase tracking-wider">
                  Son Değişim
                </div>
                <div className="flex items-baseline gap-2">
                  <span
                    className={cn(
                      "font-bold text-xl",
                      progress && progress.value > 0
                        ? "text-green-500"
                        : progress && progress.value < 0
                          ? "text-red-500"
                          : "text-muted-foreground",
                    )}
                  >
                    {progress
                      ? progress.value > 0
                        ? `+${progress.value}`
                        : progress.value
                      : "-"}
                  </span>
                  {progress && progress.value !== 0 && (
                    <span
                      className={cn(
                        "flex items-center rounded px-1 text-[10px]",
                        progress.value > 0
                          ? "bg-green-500/10 text-green-600"
                          : "bg-red-500/10 text-red-600",
                      )}
                    >
                      %{Math.abs(progress.percentage)}
                    </span>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 overflow-y-auto px-4 py-4">
            {isLoading ? (
              <div className="space-y-4">
                <Skeleton className="h-[200px] w-full rounded-xl" />
                {[1, 2].map((i) => (
                  <div key={i} className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-16 w-full" />
                  </div>
                ))}
              </div>
            ) : sessionList.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="mb-4 rounded-full bg-muted p-4">
                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg">Henüz Veri Yok</h3>
                <p className="text-muted-foreground">
                  Bu hareket için henüz kaydedilmiş bir antrenman bulunamadı.
                </p>
              </div>
            ) : (
              <Tabs defaultValue="chart" className="w-full">
                <TabsList className="mb-6 grid w-full grid-cols-2">
                  <TabsTrigger value="chart">Grafik</TabsTrigger>
                  <TabsTrigger value="list">Liste</TabsTrigger>
                </TabsList>

                <TabsContent value="chart" className="space-y-6">
                  <HistoryChart data={chartData} />

                  {progress && progress.value > 0 && (
                    <div className="flex items-start gap-3 rounded-xl border border-green-500/10 bg-green-500/5 p-4">
                      <div className="shrink-0 rounded-full bg-green-500/10 p-2 text-green-600">
                        <Zap className="h-4 w-4" />
                      </div>
                      <div>
                        <div className="font-bold text-green-700 text-sm">
                          Gelişim Tespit Edildi!
                        </div>
                        <div className="text-green-600/80 text-xs">
                          Son antrenmanda e1RM değerin %{progress.percentage}{" "}
                          arttı. Tebrikler!
                        </div>
                      </div>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="list" className="space-y-6">
                  {sessionList.map((session) => (
                    <div key={session.date.toString()} className="space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex flex-col gap-0.5">
                          <div className="flex items-center gap-2 font-bold text-foreground text-sm">
                            {session.weekNumber && session.dayNumber ? (
                              <span>
                                Hafta {session.weekNumber} Gün{" "}
                                {session.dayNumber}
                                {session.dayName ? `: ${session.dayName}` : ""}
                              </span>
                            ) : (
                              <span>Özel Antrenman</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 font-medium text-[10px] text-muted-foreground uppercase">
                            <Calendar className="h-3 w-3" />
                            {format(
                              new Date(session.date),
                              "d MMMM yyyy, EEEE",
                              {
                                locale: tr,
                              },
                            )}
                          </div>
                        </div>
                        {session.bestE1RM > 0 && (
                          <Badge
                            variant="secondary"
                            className="font-mono text-[10px]"
                          >
                            {session.bestE1RM} kg e1RM
                          </Badge>
                        )}
                      </div>

                      <div className="overflow-hidden rounded-xl border bg-card/50 shadow-sm">
                        <table className="w-full text-sm">
                          <thead className="bg-muted text-[10px] text-muted-foreground uppercase tracking-wider">
                            <tr>
                              <th className="px-3 py-2 text-left font-semibold">
                                Set
                              </th>
                              <th className="px-3 py-2 text-center font-semibold text-foreground">
                                Kg
                              </th>
                              <th className="px-3 py-2 text-center font-semibold">
                                Tekrar
                              </th>
                              <th className="px-3 py-2 text-right font-semibold">
                                RPE
                              </th>
                              <th className="px-3 py-2 text-right font-semibold">
                                Video
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y border-t">
                            {session.sets
                              .sort((a, b) => a.setNumber - b.setNumber)
                              .map((set) => (
                                <tr
                                  key={set.logId}
                                  className="hover:bg-muted/10"
                                >
                                  <td className="px-3 py-2 font-mono text-muted-foreground text-xs opacity-50">
                                    #{set.setNumber}
                                  </td>
                                  <td className="px-3 py-2 text-center font-bold tabular-nums">
                                    {set.completedWeight ?? "-"}
                                  </td>
                                  <td className="px-3 py-2 text-center font-medium opacity-80">
                                    {set.completedReps ?? "-"}
                                  </td>
                                  <td className="px-3 py-2 text-right">
                                    {set.completedRPE ? (
                                      <span className="rounded border border-primary/10 bg-primary/5 px-1.5 py-0.5 font-medium text-primary text-xs">
                                        {set.completedRPE}
                                      </span>
                                    ) : (
                                      "-"
                                    )}
                                  </td>
                                  <td className="px-3 py-1 text-right">
                                    {set.videoUrl &&
                                    set.videoProcessingStatus === "done" ? (
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 rounded-full text-primary hover:bg-primary/10 hover:text-primary"
                                        onClick={() =>
                                          setActiveVideo({
                                            url: set.videoUrl!,
                                            setNumber: set.setNumber,
                                          })
                                        }
                                      >
                                        <PlayCircle className="h-5 w-5" />
                                      </Button>
                                    ) : set.videoProcessingStatus ===
                                      "processing" ? (
                                      <Loader2 className="ml-auto h-4 w-4 animate-spin text-muted-foreground" />
                                    ) : null}
                                  </td>
                                </tr>
                              ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </TabsContent>
              </Tabs>
            )}
          </div>

          <div className="border-t bg-background p-4">
            <button
              type="button"
              className="w-full rounded-xl bg-secondary py-3.5 font-bold text-secondary-foreground transition-all hover:bg-secondary/80 active:scale-[0.98]"
              onClick={() => onOpenChange(false)}
            >
              Kapat
            </button>
          </div>
        </div>
      </DrawerContent>

      {/* Video Playback Modal */}
      <Dialog
        open={!!activeVideo}
        onOpenChange={(open) => !open && setActiveVideo(null)}
      >
        <DialogContent className="max-w-3xl overflow-hidden rounded-xl border bg-background p-0">
          <DialogHeader className="border-b px-6 py-4">
            <div className="flex items-center gap-3 overflow-hidden text-left">
              <span className="truncate font-bold text-base text-foreground">
                {exerciseName}
              </span>
              {activeVideo && (
                <div className="flex h-5 items-center justify-center rounded bg-muted px-2 font-mono font-semibold text-[10px] text-muted-foreground uppercase">
                  SET {activeVideo.setNumber}
                </div>
              )}
            </div>
          </DialogHeader>
          <div className="relative aspect-video w-full bg-black">
            {activeVideo && (
              <video
                src={activeVideo.url}
                className="h-full w-full object-contain"
                controls
                autoPlay
                playsInline
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Drawer>
  );
}
